/**
 * Sichtbarer Rotationsstart-Status im Generator-Tab (G-17):
 * Schichtmodell wählen → zeigt die aktuelle Belegung dieser KW („Früh=Team B …“)
 * und bietet „Startbelegung ändern“. Änderbar nur, solange noch KEINE
 * Wochenpläne existieren (sonst ist der Rotationsstart festgelegt → Button
 * ausgegraut mit Tooltip). Gate: personal_schreiben.
 */

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { CalendarClock } from "lucide-react"
import { StartAssignmentSheet } from "@/components/planning/StartAssignmentSheet"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { isoWeekOf, todayIso } from "@/lib/isoWeek"
import { api } from "@/services/api"

export function RotationStartCard() {
  const [shiftModelId, setShiftModelId] = useState("")
  const [startOpen, setStartOpen] = useState(false)
  const current = useMemo(() => isoWeekOf(todayIso()), [])

  const models = useQuery({
    queryKey: ["shiftModels", { size: 100 }],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
  })

  // Status inkl. aktueller Belegung (currentAssignment nur bei gesetztem Anker + year/week).
  const rotation = useQuery({
    queryKey: ["rotationStatus", Number(shiftModelId), current.year, current.week],
    queryFn: () => api.planning.rotation.status(Number(shiftModelId), current.year, current.week),
    enabled: shiftModelId !== "",
  })

  const status = rotation.data
  const modelName = (models.data?.data ?? []).find((m) => m.id === shiftModelId)?.name

  const belegung = useMemo(() => {
    if (!status?.currentAssignment) return null
    const teamName = (id: string) => status.teams.find((t) => t.id === id)?.name ?? id
    const shiftName = (id: string) => status.shifts.find((s) => s.id === id)?.name ?? id
    return status.currentAssignment
      .map((a) => `${shiftName(a.shiftId)}=${teamName(a.teamId)}`)
      .join(", ")
  }, [status])

  return (
    <Card className="md:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="size-4 text-muted-foreground" /> Rotationsstart
          (Startbelegung)
        </CardTitle>
        <CardDescription>
          Legt fest, welches Team zum Start in welcher Schicht arbeitet — die Rotation
          setzt phasenrichtig darauf auf. Nur änderbar, solange noch keine Wochenpläne
          existieren (G-17).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="max-w-xs space-y-1.5">
          <Label>Schichtmodell</Label>
          <Select value={shiftModelId} onChange={(e) => setShiftModelId(e.target.value)}>
            <option value="">— Schichtmodell wählen —</option>
            {(models.data?.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.shiftType})
              </option>
            ))}
          </Select>
        </div>

        {shiftModelId !== "" && rotation.isSuccess && status && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {status.hasAnchor ? (
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Rotationsstart gesetzt.
                </span>{" "}
                Aktuelle Belegung (KW {current.week}/{current.year})
                {belegung ? `: ${belegung}` : " — nicht verfügbar."}
              </p>
            ) : (
              <p className="text-muted-foreground">
                Noch kein Rotationsstart festgelegt — beim ersten Generieren wird die
                Startbelegung erfragt.
              </p>
            )}
          </div>
        )}

        {shiftModelId !== "" && status && (
          <div
            title={
              status.hasWeekPlans
                ? "Es existieren bereits Schichtpläne — der Rotationsstart ist festgelegt und kann nicht mehr geändert werden."
                : undefined
            }
          >
            <Button
              variant="outline"
              disabled={status.hasWeekPlans}
              onClick={() => setStartOpen(true)}
            >
              {status.hasAnchor ? "Startbelegung ändern …" : "Startbelegung festlegen …"}
            </Button>
          </div>
        )}
      </CardContent>

      {status && (
        <StartAssignmentSheet
          open={startOpen}
          onOpenChange={setStartOpen}
          modelId={Number(shiftModelId)}
          modelName={modelName}
          status={status}
        />
      )}
    </Card>
  )
}
